import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS, Transform } from "@dnd-kit/utilities";
import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";

import Button from "components/ui/button/Button";
import TooltipComponent from "components/ui/tooltip/Tooltip";
import { BidQuestionObj } from "lib/hooks/api/bid/bid_question/useBidQuestions";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { Flex, Text } from "../../../styles/utility-components";

type Props = {
  question: BidQuestionObj;
  openEditDrawer: () => void;
  openDeleteModal: () => void;
};

function BidQuestionItem({ question, openEditDrawer, openDeleteModal }: Props) {
  const { colors } = useTheme();
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: question.id,
    disabled: !question.teamId, // disable dragging for default questions
  });

  return (
    <QuestionCell
      key={question.id}
      isDefault={!question.teamId}
      transform={transform}
      transition={transition}
      ref={setNodeRef}
      {...attributes}
      {...listeners}
    >
      {question.teamId && (
        <DragIconContainer>
          <UIcon icon="dragIcon" color={colors.sysTextSecondary} size={16} />
        </DragIconContainer>
      )}
      <Flex gap={24} alignItems="center">
        <QuestionTitle color="inherit">{question.title}</QuestionTitle>
        <QuestionText color="inherit">{question.question}</QuestionText>
      </Flex>
      {question.teamId ? (
        <Flex alignItems="center">
          <Label>
            <Text description fontWeight="500" color={colors.sysTextSecondary} ellipsis>
              Custom criteria
            </Text>
          </Label>

          <Button icon="edit02" buttonStyle="inverse" onClick={openEditDrawer} />

          <Button icon="trash03" buttonStyle="inverse" onClick={openDeleteModal} />
        </Flex>
      ) : (
        <TooltipComponent
          delayDuration={300}
          content="Default criteria cannot be edited"
          side="bottom"
          variant="dark"
        >
          {/* Annoying, but tooltip doesn't appear unless wrapped in a span */}
          <span>
            <UIcon icon="helpCircle" size={16} color="sysTextSecondary" />
          </span>
        </TooltipComponent>
      )}
    </QuestionCell>
  );
}

const QuestionCell = styled(Flex)<{
  isDefault: boolean;
  transition: string | undefined;
  transform: Transform | null;
}>(({ theme, isDefault, transform, transition }) => ({
  position: "relative",
  alignItems: "center",
  gap: 24,
  padding: 16,
  paddingRight: isDefault ? 16 : 8,
  justifyContent: "space-between",
  borderRadius: 4,
  color: isDefault ? theme.colors.sysTextSecondary : theme.colors.sysTextDefault,
  backgroundColor: isDefault
    ? theme.colors.sysBackgroundAlternative
    : theme.colors.sysBackgroundDefault,
  transition,
  transform: CSS.Transform.toString(transform),
  ...(!isDefault && {
    cursor: "pointer",

    "&:hover": {
      border: `1px solid ${theme.colors.sysBorderDecorative}`,
    },
    "&:active": {
      cursor: "grabbing",
    },
  }),
}));

const DragIconContainer = styled.div(({ theme }) => ({
  position: "absolute",
  left: -8, // move it into the border or slightly out (adjust as needed)
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 10,
  display: "none", // hidden by default
  pointerEvents: "auto", // so it can still capture clicks
  // Show when QuestionCell is hovered
  [`${QuestionCell}:hover &`]: {
    display: "block",
    backgroundColor: theme.colors.sysBackgroundDefault,
    border: `1px solid ${theme.colors.sysBorderDecorative}`,
    borderRadius: 2,
    paddingTop: 4,
    cursor: "grab",
    "&:active": {
      cursor: "grabbing",
    },
  },
}));

const QuestionTitle = styled(Text)({
  minWidth: 120,
  width: 120,
});

const QuestionText = styled(Text)({
  maxWidth: 600,
});

const Label = styled(Flex)(({ theme }) => ({
  alignItems: "center",
  padding: "2px 8px",
  border: `1px solid ${theme.colors.sysTextSecondary}`,
  borderRadius: 16,
  marginRight: 8,
}));

export default BidQuestionItem;
